import { useState } from "react";
import { getProjectById } from "@/features/leaderboard/api/leaderboard-api.ts";
import type { Project } from "@/features/leaderboard/types/index.ts";

interface ProjectNameLinkProps {
  projectId: string;
  label: string;
  onOpenProjectDetail?: (project: Project) => void;
  className?: string;
}

/**
 * Clickable project name for Intelligence tab tables. On click,
 * fetches the full `Project` via `getProjectById` and opens the
 * shared detail modal via the handler that `App.tsx` passes down.
 *
 * When `onOpenProjectDetail` is not provided (e.g. the Intel page
 * is being rendered outside the app shell), the link degrades to
 * plain text so the table still reads cleanly.
 */
export function ProjectNameLink({
  projectId,
  label,
  onOpenProjectDetail,
  className,
}: ProjectNameLinkProps) {
  const [loading, setLoading] = useState(false);

  if (!onOpenProjectDetail) {
    return <span className={className}>{label}</span>;
  }

  const handleClick = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const project = await getProjectById(projectId);
      if (project) {
        onOpenProjectDetail(project);
      } else {
        console.warn(`ProjectNameLink: project ${projectId} not found`);
      }
    } catch (err) {
      console.error(`ProjectNameLink: failed to load project ${projectId}`, err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      type="button"
      className={`intel-project-link${className ? ` ${className}` : ""}${loading ? " is-loading" : ""}`}
      onClick={handleClick}
      aria-label={`Open details for ${label}`}
      disabled={loading}
    >
      {label}
    </button>
  );
}
